// src/ui/LoopLibrary.js
//
// The Loops dialog: starter MIDI loops by kind, each with a Hear and an Add (docs/learning-curve.md). This file is the part that needs no page:
// reading and checking the library, grouping it, turning a loop into the one change that adds it as a clip, scheduling its notes for Hear, and the
// dialog's markup. Fetching the file, finding the track and loading an instrument for a loop with nowhere to go are web/app/Loops.js.
//
// The library is web/loops/index.json, generated by bin/write-loops.js. It is read strictly: a loop that is malformed is refused with its id and the
// reason, never repaired, since a note outside the loop or a pitch outside MIDI would be added to a person's piece and be wrong there.

/** The kinds, in the order the dialog lists them, with the plugin directory whose instrument suits a loop of that kind when there is no track for it. */
export const KINDS = Object.freeze([
  { kind: 'drums', heading: 'Drums', voice: 'drumkit' },
  { kind: 'bass', heading: 'Bass', voice: 'pulse' },
  { kind: 'chords', heading: 'Chords', voice: 'canticle' },
  { kind: 'melody', heading: 'Melody', voice: 'moka' }
])

const fail = (loop, message) => { throw new Error(`loop ${loop?.id ?? '(no id)'}: ${message}`) }

/** Check one loop, as the library file gives it, and return it. */
export function checkLoop (loop) {
  if (!loop || typeof loop !== 'object') fail(loop, 'is not an object')
  if (typeof loop.id !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(loop.id)) fail(loop, 'needs an id of lowercase letters, digits and hyphens')
  if (typeof loop.label !== 'string' || loop.label.trim() === '') fail(loop, 'needs a label')
  if (!KINDS.some(k => k.kind === loop.kind)) fail(loop, `has kind ${loop.kind}, which is not one of ${KINDS.map(k => k.kind).join(', ')}`)
  if (typeof loop.about !== 'string' || loop.about.trim() === '') fail(loop, 'needs a line saying what it is')
  if (typeof loop.licence !== 'string' || loop.licence.trim() === '') fail(loop, 'needs a licence')
  if (!(Number.isFinite(loop.lengthBeats) && loop.lengthBeats > 0)) fail(loop, 'needs a length in beats above zero')
  if (loop.key !== undefined && (typeof loop.key !== 'string' || loop.key.trim() === '')) fail(loop, 'has a key that is not a name')
  if (loop.bpm !== undefined && !(Number.isFinite(loop.bpm) && loop.bpm > 0)) fail(loop, 'has a tempo that is not above zero')
  // A suggested level for the track the loop gets when it is added with a track of its own, so the parts of a set start in balance.
  if (loop.gain !== undefined && !(Number.isFinite(loop.gain) && loop.gain >= 0 && loop.gain <= 2)) fail(loop, 'has a gain that is not from 0 to 2')
  // The directory of the plugin that should play this loop, where it asks for one other than the kind's usual voice.
  if (loop.voice !== undefined && !(typeof loop.voice === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(loop.voice))) fail(loop, 'has a voice that is not a plugin directory name')
  if (!Array.isArray(loop.notes) || loop.notes.length === 0) fail(loop, 'needs at least one note')
  for (const [i, note] of loop.notes.entries()) {
    const where = `note ${i}`
    if (!(Number.isFinite(note.startBeat) && note.startBeat >= 0)) fail(loop, `${where} starts before the loop`)
    if (!(Number.isFinite(note.lengthBeats) && note.lengthBeats > 0)) fail(loop, `${where} has no length`)
    if (note.startBeat >= loop.lengthBeats) fail(loop, `${where} starts at or after the end of the loop`)
    if (note.startBeat + note.lengthBeats > loop.lengthBeats + 1e-9) fail(loop, `${where} runs past the end of the loop`)
    if (!(Number.isInteger(note.pitch) && note.pitch >= 0 && note.pitch <= 127)) fail(loop, `${where} has pitch ${note.pitch}, which is not a MIDI note`)
    if (!(Number.isInteger(note.velocity) && note.velocity >= 1 && note.velocity <= 127)) fail(loop, `${where} has velocity ${note.velocity}, which is not 1 to 127`)
  }
  return loop
}

/** The loops of a library file, each checked, with no two sharing an id. */
export function loopsFromIndex (index) {
  if (!index || !Array.isArray(index.loops)) throw new Error('the loop library has no loops list')
  const loops = index.loops.map(checkLoop)
  const seen = new Set()
  for (const loop of loops) {
    if (seen.has(loop.id)) throw new Error(`loop ${loop.id}: another loop has the same id`)
    seen.add(loop.id)
  }
  return loops
}

/**
 * The sets of a library file, each a few loops added together on a track of their own at a tempo (a deep house starter: drums, bass, stabs and a
 * pad). A set is checked against the loops it names, which must all be there, and must be at least two, since one loop is a loop.
 */
export function setsFromIndex (index, loops) {
  const known = new Set(loops.map(l => l.id))
  const sets = index.sets ?? []
  if (!Array.isArray(sets)) throw new Error('the loop library sets are not a list')
  const seen = new Set()
  return sets.map(set => {
    const fail = message => { throw new Error(`set ${set?.id ?? '(no id)'}: ${message}`) }
    if (!set || typeof set.id !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(set.id)) fail('needs an id of lowercase letters, digits and hyphens')
    if (seen.has(set.id)) fail('another set has the same id')
    seen.add(set.id)
    if (typeof set.label !== 'string' || set.label.trim() === '') fail('needs a label')
    if (typeof set.about !== 'string' || set.about.trim() === '') fail('needs a line saying what it is')
    if (typeof set.licence !== 'string' || set.licence.trim() === '') fail('needs a licence')
    if (!(Number.isFinite(set.bpm) && set.bpm > 0)) fail('needs a tempo above zero')
    if (!Array.isArray(set.loops) || set.loops.length < 2) fail('needs at least two loops')
    for (const id of set.loops) if (!known.has(id)) fail(`names ${id}, which is not a loop`)
    if (new Set(set.loops).size !== set.loops.length) fail('names a loop twice')
    return set
  })
}

/** The loops under a heading for each kind that has any, in the order of KINDS. */
export function groupsOf (loops) {
  return KINDS
    .map(k => ({ heading: k.heading, kind: k.kind, loops: loops.filter(l => l.kind === k.kind) }))
    .filter(group => group.loops.length > 0)
}

/** How long a loop is, in words: "2 bars", or "6 beats" when it is not a whole number of bars, then its key where it has one. */
export function lengthText (loop, beatsPerBar = 4) {
  const bars = loop.lengthBeats / beatsPerBar
  const length = Number.isInteger(bars) ? `${bars} ${bars === 1 ? 'bar' : 'bars'}` : `${loop.lengthBeats} beats`
  return loop.key ? `${length}, ${loop.key}` : length
}

/** A set in words: how many tracks, how long, and the tempo and key it asks for. */
export function setText (set, loops) {
  const parts = loops.filter(l => set.loops.includes(l.id))
  const beats = Math.max(...parts.map(l => l.lengthBeats))
  const bars = beats / 4
  return `${set.loops.length} tracks, ${Number.isInteger(bars) ? `${bars} bars` : `${beats} beats`}, ${set.bpm} BPM${set.key ? `, ${set.key}` : ''}`
}

/**
 * The one change that adds a loop as a clip at `startBeat` on `track`. The notes are copied, so a clip edited afterwards does not change the
 * library it came from.
 */
export function addClipChange ({ loop, track, startBeat }) {
  if (!(Number.isFinite(startBeat) && startBeat >= 0)) throw new Error('a loop is added at a beat at or after the start')
  return {
    op: 'addClip',
    track,
    kind: 'midi',
    startBeat,
    lengthBeats: loop.lengthBeats,
    notes: loop.notes.map(n => ({ startBeat: n.startBeat, lengthBeats: n.lengthBeats, pitch: n.pitch, velocity: n.velocity }))
  }
}

/**
 * The note on and note off events that play a loop once, as stream positions from `startFrame` at `bpm`, in time order, for Hear. A note off
 * is sent for every note on, after it, so nothing is left sounding when the loop ends.
 */
export function eventsFor (loop, { bpm, sampleRate, startFrame }) {
  if (!(bpm > 0 && sampleRate > 0)) throw new Error('events need a tempo and a sample rate above zero')
  const frame = beat => startFrame + Math.round(beat * (60 / bpm) * sampleRate)
  const events = loop.notes.flatMap(n => [
    { frame: frame(n.startBeat), order: 1, bytes: Uint8Array.from([0x90, n.pitch, n.velocity]) },
    { frame: frame(n.startBeat + n.lengthBeats), order: 0, bytes: Uint8Array.from([0x80, n.pitch, 0]) }
  ])
  // A note off before a note on at the same frame, so a repeated pitch is cut and then struck, not struck and then cut.
  events.sort((a, b) => a.frame - b.frame || a.order - b.order)
  return events.map(({ frame, bytes }) => ({ frame, bytes }))
}

/**
 * The dialog. `groups` is from groupsOf. `canHear` says whether there is an instrument to play a loop through: a boolean, or a function of the
 * loop, since a drum loop and a bass loop want different instruments. Where there is not, Hear is left out for that loop, and the text says what to
 * do, rather than a button that cannot act. `sets` are added whole with an Add of their own and no Hear, since a set is several parts that sound
 * together once added. Returns `{ element, say(text) }`.
 */
export function createLoopBrowser (document, { groups, sets = [], loops = [], canHear, beatsPerBar = 4, onHear, onAdd, onAddSet = () => {}, onClose }) {
  const dialog = document.createElement('dialog')
  dialog.className = 'chooser loops'
  dialog.setAttribute('aria-labelledby', 'loops-title')

  const title = document.createElement('h2')
  title.id = 'loops-title'
  title.textContent = 'Loops'
  const hearable = loop => (typeof canHear === 'function' ? canHear(loop) : canHear) === true
  const anyHearable = groups.some(g => g.loops.some(hearable))
  const help = document.createElement('p')
  help.className = 'note'
  help.textContent = anyHearable
    ? 'Starting points, each in the key it names: add one, then change it in the piano roll. Hear plays a loop through the instrument on the selected track.'
    : 'Starting points, each in the key it names: add one, then change it in the piano roll. Add puts it on the selected track that has an instrument, or on a new track with a sound to suit it. Select a track with an instrument to hear a loop before adding it.'
  const status = document.createElement('p')
  status.className = 'note'
  status.setAttribute('role', 'status')

  const scroll = document.createElement('div')
  scroll.className = 'sound-scroll'
  if (sets.length > 0) {
    const section = document.createElement('section')
    const heading = document.createElement('h3')
    heading.id = 'loop-sets'
    heading.textContent = 'Starter sets'
    const list = document.createElement('ul')
    list.className = 'sound-list'
    list.setAttribute('aria-labelledby', heading.id)
    for (const set of sets) {
      const item = document.createElement('li')
      const words = document.createElement('div')
      words.className = 'sound-words'
      const name = document.createElement('strong')
      name.textContent = `${set.label} `
      const mark = document.createElement('span')
      mark.className = 'sound-mark'
      mark.textContent = `(${setText(set, loops)})`
      const about = document.createElement('span')
      about.className = 'sound-about'
      about.textContent = set.about
      words.append(name, mark, about)
      const add = document.createElement('button')
      add.type = 'button'
      add.textContent = 'Add all'
      add.setAttribute('aria-label', `Add all of ${set.label}`)
      add.addEventListener('click', () => onAddSet(set))
      item.append(words, add)
      list.append(item)
    }
    section.append(heading, list)
    scroll.append(section)
  }
  groups.forEach((group, index) => {
    const section = document.createElement('section')
    const heading = document.createElement('h3')
    heading.id = `loop-group-${index}`
    heading.textContent = group.heading
    const list = document.createElement('ul')
    list.className = 'sound-list'
    list.setAttribute('aria-labelledby', heading.id)
    for (const loop of group.loops) {
      const item = document.createElement('li')
      const words = document.createElement('div')
      words.className = 'sound-words'
      const name = document.createElement('strong')
      name.textContent = `${loop.label} `
      const length = document.createElement('span')
      length.className = 'sound-mark'
      length.textContent = `(${lengthText(loop, beatsPerBar)})`
      const about = document.createElement('span')
      about.className = 'sound-about'
      about.textContent = loop.about
      words.append(name, length, about)
      const buttons = []
      if (hearable(loop)) {
        const hear = document.createElement('button')
        hear.type = 'button'
        hear.textContent = 'Hear'
        hear.setAttribute('aria-label', `Hear ${loop.label}`)
        hear.addEventListener('click', () => onHear(loop))
        buttons.push(hear)
      }
      const add = document.createElement('button')
      add.type = 'button'
      add.textContent = 'Add'
      add.setAttribute('aria-label', `Add ${loop.label}`)
      add.addEventListener('click', () => onAdd(loop))
      buttons.push(add)
      item.append(words, ...buttons)
      list.append(item)
    }
    section.append(heading, list)
    scroll.append(section)
  })
  if (groups.length === 0 && sets.length === 0) {
    const none = document.createElement('p')
    none.className = 'note'
    none.textContent = 'There are no loops to offer.'
    scroll.append(none)
  }

  const buttons = document.createElement('div')
  buttons.className = 'consent-buttons'
  const close = document.createElement('button')
  close.type = 'button'
  close.textContent = 'Close'
  close.addEventListener('click', () => onClose())
  buttons.append(close)

  dialog.append(title, help, status, scroll, buttons)
  // Escape, and any other way of dismissing it, is Close.
  dialog.addEventListener('cancel', () => onClose())

  return { element: dialog, say (text) { status.textContent = text } }
}
